import React, { useEffect, useState } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Row,
  Col,
  InputGroup,
  Form,
  Button,
  Spinner,
  Container,
  Card,
} from "react-bootstrap";
import { Modal, ModalContent } from "../../components/Modal";
import { IconBrandVisa, IconCurrencyDollar } from "@tabler/icons-react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import ErrorModal from "./ErrorModal";
import SuccessModal from "./SuccessModal";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import auth_background from "../../assets/auth_background.webp";
import useIdleLogout from "../../hooks/useIdleLogout";
import { usePostHog } from "@posthog/react";

const validationSchema = Yup.object().shape({
  cardHolderName: Yup.string()
    .required("Cardholder name is required")
    .matches(/^[A-Za-z\s]+$/, "Only letters and spaces are allowed"),
  cardNumber: Yup.string()
    .required("Card number is required")
    .matches(/^\d{15,16}$/, "Card number must be 15 or 16 digits"),
  // expiry: Yup.string().required("Expiry is required"),
  expiry: Yup.string()
    .required("Expiry is required")
    .matches(/^\d{4}$/, "Expiry must be in MM/YY format")
    .test("not-expired", "Expiry date cannot be in the past", function (value) {
      if (!value || value.length !== 4) return false;

      const monthStr = value.slice(0, 2);
      const yearStr = value.slice(2);

      const enteredMonth = parseInt(monthStr, 10);
      const enteredYear = parseInt(yearStr, 10);

      if (isNaN(enteredMonth) || isNaN(enteredYear)) return false;
      if (enteredMonth < 1 || enteredMonth > 12) return false;

      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYearLast2Digits = now.getFullYear() % 100;

      if (enteredYear < currentYearLast2Digits) return false;
      if (enteredYear === currentYearLast2Digits && enteredMonth < currentMonth)
        return false;

      return true;
    }),

  // cvc: Yup.string()
  //   .required("CVC is required")
  //   .matches(/^\d{3}$/, "CVC must be 3 digits"),
  cvc: Yup.string()
    .nullable()
    .notRequired()
    .test("cvc-format", "CVC must be 3 digits", (value) => {
      if (!value) return true; // allow empty
      return /^\d{3}$/.test(value);
    }),

  address: Yup.string().nullable().notRequired(),

  zipCode: Yup.string()
    .required("Zip Code is required")
    .matches(/^\d{5,6}$/, "Zip code must be 5 or 6 digits"),
  // .email("Invalid email format"),
  invoiceNumber: Yup.string().required("Invoice Number is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .positive("Amount must be greater than zero"),
  selectedEmail: Yup.string().optional(),
  customEmail: Yup.string().optional().email("Invalid email format"),
  notes: Yup.string().optional(),
});

function LinkCard({ auto_id }) {
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const posthog = usePostHog();
  const { token } = useParams();

  const { isLoading, postData } = usePostRequest(
    API_ENDPOINTS.CREATE_CARD_INFO_EXTERNAL,
  );

  const { postData: verifyToken } = usePostRequest(API_ENDPOINTS.VERIFY_TOKEN);

  useEffect(() => {
    if (token) {
      verifyToken(
        { token },
        (response) => {
          setTokenError(null);
          setTokenData(response || null);
        },
        (error) =>
          setTokenError(error?.message || "Token verification failed."),
      );
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      posthog.stopSessionRecording();
    } else {
      posthog.startSessionRecording();
    }
  }, [token]);

  // useEffect(() => {
  //   if (token) {
  //     verifyToken(
  //       { token },
  //       () => setTokenError(null),
  //       (error) => setTokenError(error?.message || "Token verification failed.")
  //     );
  //   }
  // }, [token]);

  console.log("tokenData", tokenData);

  useIdleLogout();
  useEffect(() => {
    if (tokenData) {
      setValue("invoiceNumber", tokenData?.other_id || "");
      setValue("amount", tokenData?.data?.total_cost_after_margin_sales || "");
      setValue("cardHolderName", tokenData?.data?.ko_name || "");
    }
  }, [tokenData, setValue]);

  const onSubmit = (data) => {
    const primaryEmail =
      data.selectedEmail === "OTHER" ? data.customEmail : data.selectedEmail;
    const payload = {
      token,
      info_number: data.cardNumber,
      info_expiry: data.expiry,
      info_name: data.cardHolderName,
      cvc_code: data.cvc || null,
      zip_code: data.zipCode,
      // customer_email_addresses: data.customerEmail || "",
      invoice_number: data.invoiceNumber,
      amount: data.amount,
      // email_address: primaryEmail || null,
      note: data.notes || null,
      address: data.address || null,
    };

    postData(
      payload,
      (response) => {
        toast.success(response?.message);
        reset();
        setShowSuccessModal(true);
        posthog.capture("payment_details_added", {
          added_via: "EXTERNAL LINK",
        });
      },
      (error) => toast.error(error?.message),
    );
  };

  if (tokenError) {
    return (
      <ErrorModal isOpen={true} errorMessage={tokenError} redirectPath="/" />
    );
  }

  return (
    <>
      {/* <Modal className="add_credit_card mb-3" isOpen={true}>
        <ModalContent> */}
      <div
        style={{
          backgroundImage: `url(${auth_background})`,
        }}
        className="full_modal_bg vh-100"
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={10}>
              <Card>
                <Card.Body className="p-5">
                  <h4>
                    <b>Payment Card Details</b>
                  </h4>
                  <Card.Text className="mb-4">
                    Description: This page is fully secure with encryption to
                    protect your information. We take your privacy seriously,
                    and your card details will only be used to complete the
                    transaction of requested order.
                  </Card.Text>

                  <FormProvider {...methods}>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              Cardholder Name
                            </Form.Label>
                            <Controller
                              name="cardHolderName"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  placeholder="Enter Cardholder Name"
                                />
                              )}
                            />
                            {errors.cardHolderName && (
                              <div className="text-danger">
                                {errors.cardHolderName.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              Card Number
                            </Form.Label>
                            <InputGroup>
                              <InputGroup.Text>
                                <IconBrandVisa stroke={1} />
                              </InputGroup.Text>
                              <Controller
                                name="cardNumber"
                                control={control}
                                render={({
                                  field: { onChange, value, ...rest },
                                }) => {
                                  const formattedValue = value
                                    ?.replace(/\D/g, "")
                                    .slice(0, 16)
                                    .replace(/(.{4})/g, "$1 ")
                                    .trim();
                                  return (
                                    <Form.Control
                                      {...rest}
                                      value={formattedValue}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 16),
                                        )
                                      }
                                      placeholder="**** **** **** ****"
                                    />
                                  );
                                }}
                              />
                            </InputGroup>
                            {errors.cardNumber && (
                              <div className="text-danger">
                                {errors.cardNumber.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              Expiry Date
                            </Form.Label>
                            <Controller
                              name="expiry"
                              control={control}
                              render={({
                                field: { onChange, value, ...rest },
                              }) => {
                                let clean = (value || "")
                                  .replace(/\D/g, "")
                                  .slice(0, 4);
                                let formatted = clean;
                                if (clean.length >= 3) {
                                  formatted = `${clean.slice(
                                    0,
                                    2,
                                  )}/${clean.slice(2)}`;
                                }

                                return (
                                  <Form.Control
                                    {...rest}
                                    value={formatted}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          .replace(/\D/g, "")
                                          .slice(0, 4),
                                      )
                                    }
                                    placeholder="MM/YY"
                                  />
                                );
                              }}
                            />
                            {errors.expiry && (
                              <div className="text-danger">
                                {errors.expiry.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label className="">CVC Code</Form.Label>
                            <Controller
                              name="cvc"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="password"
                                  placeholder="123"
                                  maxLength={3}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 3),
                                    )
                                  }
                                />
                              )}
                            />
                            {errors.cvc && (
                              <div className="text-danger">
                                {errors.cvc.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              ZIP Code
                            </Form.Label>
                            <Controller
                              name="zipCode"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  placeholder="Enter Zip Code"
                                  maxLength={6}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                    )
                                  }
                                />
                              )}
                            />
                            {errors.zipCode && (
                              <div className="text-danger">
                                {errors.zipCode.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Controller
                              name="address"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  as="textarea"
                                  rows={2}
                                  placeholder="Enter Address"
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>

                        {/* <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              Customer email address(es)
                            </Form.Label>
                            <Form.Text>
                              Please enter the email as it apppears on the Quote
                              or Invoice, separate the emails with commas like -{" "}
                              <span className="fw-semibold text-primary">
                                customer@aspettoinc.com, xyz@companyname.com
                              </span>
                            </Form.Text>
                            <Controller
                              name="customerEmail"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="text"
                                  placeholder="Enter Customer Email"
                                />
                              )}
                            />
                            {errors.customerEmail && (
                              <div className="text-danger">
                                {errors.customerEmail.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col> */}

                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">
                              Invoice Number
                            </Form.Label>
                            <Controller
                              name="invoiceNumber"
                              control={control}
                              disabled={tokenData?.other_id ? true : false}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="text"
                                  placeholder="Enter Invoice Number"
                                />
                              )}
                            />
                            {errors.invoiceNumber && (
                              <div className="text-danger">
                                {errors.invoiceNumber.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">Amount</Form.Label>
                            <InputGroup>
                              <InputGroup.Text>
                                <IconCurrencyDollar stroke={2} />
                              </InputGroup.Text>
                              <Controller
                                name="amount"
                                control={control}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="number"
                                    disabled={
                                      tokenData?.data
                                        ?.total_cost_after_margin_sales
                                        ? true
                                        : false
                                    }
                                    step="0.01"
                                    placeholder="Enter Amount"
                                    min="0"
                                  />
                                )}
                              />
                            </InputGroup>
                            {errors.amount && (
                              <div className="text-danger">
                                {errors.amount.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        {/* <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Controller
                              name="address"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  as="textarea"
                                  rows={2}
                                  placeholder="Enter Address"
                                />
                              )}
                            />
                          </Form.Group>
                        </Col> */}

                        {/* <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>

                            <Controller
                              name="selectedEmail"
                              control={control}
                              render={({ field: { value, onChange } }) => {
                                const emails = [
                                  "dhs_orders@aspettoinc.com",
                                  "gsa@aspettoinc.com",
                                  "hhspm@aspettoinc.com",
                                  "fbop@aspettoinc.com",
                                ];

                                return (
                                  <>
                                    {emails.map((email, index) => (
                                      <Form.Check
                                        key={index}
                                        type="radio"
                                        id={`email-${index}`}
                                      >
                                        <Form.Check.Input
                                          type="radio"
                                          id={`email-${index}`}
                                          value={email}
                                          checked={value === email}
                                          onChange={() => onChange(email)}
                                        />
                                        <Form.Check.Label
                                          htmlFor={`email-${index}`}
                                        >
                                          {email}
                                        </Form.Check.Label>
                                      </Form.Check>
                                    ))}

                                    <Form.Check
                                      className=" w-100"
                                      type="radio"
                                      id="email-other"
                                    >
                                      <Form.Check.Input
                                        type="radio"
                                        id="email-other"
                                        value="OTHER"
                                        checked={value === "OTHER"}
                                        onChange={() => onChange("OTHER")}
                                      />
                                      <Form.Check.Label
                                        className=" w-100"
                                        htmlFor="email-other"
                                      >
                                        <Controller
                                          name="customEmail"
                                          control={control}
                                          render={({ field }) => (
                                            <Form.Control
                                              {...field}
                                              type="email"
                                              placeholder="Enter custom email"
                                              className="mt-1 w-100"
                                            />
                                          )}
                                        />
                                      </Form.Check.Label>
                                    </Form.Check>
                                  </>
                                );
                              }}
                            />

                            {errors.selectedEmail && (
                              <div className="text-danger mt-1">
                                {errors.selectedEmail.message}
                              </div>
                            )}
                            {errors.customEmail && (
                              <div className="text-danger mt-1">
                                {errors.customEmail.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col> */}

                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Additional Notes</Form.Label>
                            <Controller
                              name="notes"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  as="textarea"
                                  type="text"
                                  placeholder="Enter notes"
                                />
                              )}
                            />
                            {errors.notes && (
                              <div className="text-danger">
                                {errors.notes.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          type="submit"
                          variant="warning"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                            />
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      </div>
                    </Form>
                  </FormProvider>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* </ModalContent>
      </Modal> */}

      {showSuccessModal && <SuccessModal isOpen={showSuccessModal} />}
    </>
  );
}

export default LinkCard;
